import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Container, Card, CardBody, Col, Row } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ArticleService } from "../../services/ArticleService";
import { ParameterTypes } from "../../utils/ParameterTypes";
import { ViewArticleModel } from "../../models/Article";
import moment from "moment";
import DOMPurify from "dompurify";
import CommentSection from "./ArticleComment";
import Swal from "sweetalert2";
import UserContext from "../../context/UserContext";
import likeButton from "../../assets/images/article/like2.svg";
import likedButton from "../../assets/images/article/like.svg";
import ShareModal from "./Popup/ShareModal";
import { Rating } from "react-simple-star-rating";
import Spinner from "../../common/spinner/spinner";
import defaultImage from "../../assets/images/avatar.jpeg";
import { VidemoService } from "../../services/VimeoService";
import { toast } from "react-toastify";
import { Util } from "../../Util";

const PublicArticleDetails: React.FC = () => {
  const { articleId, viewtype } = useParams<ParameterTypes>();
  const [userDetails] = useContext(UserContext);
  const [articleDetails, setArticleDetails] = useState<ViewArticleModel>();
  const [comment, setComment] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [rating1] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [transcodeStatus, setTranscodeStatus] = useState("");
  const [vimeoId, setVimeoId] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const history = useHistory();

  useEffect(() => {
    getArticleDetails(articleId);
  }, []);

  const getArticleDetails = (id: string) => {
    setIsLoading(true);

    ArticleService.getArticleByPublicId(id).then(res => {
      if (res.success) {
        setArticleDetails(res.data);
        setIsLoading(false);

        if (!res.data.uploadId) {
          try {
            setVimeoId(res.data.vimeoId!);
            refreshTranscodeStatus(res.data.vimeoId!);
          } catch (error) {
            console.log(error);
          }
        }
      } else {
        setArticleDetails(undefined);
        setIsLoading(false);
      }
    });
  };

  const refreshTranscodeStatus = (vimeoId: string) => {
    setIsRefreshing(true);

    VidemoService.checkTranscodeStatus(vimeoId).then((res: any) => {
      setTranscodeStatus(res.transcode.status);
      setIsRefreshing(false);
    });
  };

  const deleteArticle = (articleId: string) => {
    Swal.fire({
      icon: "warning",
      title: "Are you sure you want to delete?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#FD7F00",
      cancelButtonColor: "#FD7F00",
    }).then(result => {
      if (result.isConfirmed) {
        ArticleService.deleteArticle(articleId).then(res => {
          if (res.success) {
            history.push("/articles");
          } else {
            Swal.fire({
              icon: "error",
              title: res.error ? res.error : "Something went wrong. Please try again later.",
              confirmButtonColor: "#FD7F00",
            });
          }
        });
      }
    });
  };

  const redirectToEdit = (articleId: string) => {
    history.push("/create-article", { articleId: articleId });
  };

  const isLiked = () => {
    if (articleDetails?.likedBy?.includes(userDetails?._id as any)) {
      return true;
    } else {
      return false;
    }
  };

  const likePost = (postId: any) => {
    ArticleService.addLike(postId).then(res => {
      if (res.success) {
        setArticleDetails(res.data);
      } else {
        setArticleDetails(undefined);
      }
    });
  };

  const addComment = (postId: any) => {
    setComment("");
    const data = {
      _id: new Date().valueOf().toString(),
      userId: {
        firstname: userDetails?.firstname ? userDetails?.firstname : "",
        lastname: userDetails?.lastname ? userDetails?.lastname : "",
        photoId: {
          url: userDetails?.photoId?._id ? Util.fileURL(userDetails?.photoId?._id) : "",
          _id: userDetails?.photoId?._id ? userDetails?.photoId?._id : "",
          type: userDetails?.photoId?.type ? userDetails?.photoId?.type : "",
        },
        role: userDetails?.role,
        _id: userDetails?._id ? userDetails?._id : "",
      },
      comment: comment,
      replies: [],
      date: new Date().toISOString(),
    };

    articleDetails?.comments.push(data);

    const newCommentList = {
      comment: articleDetails?.comments,
    };

    ArticleService.updateCommentReply(postId, newCommentList).then(res => {
      if (res.success) {
        setArticleDetails(res.data);
      } else {
        setArticleDetails(articleDetails);
      }
    });
  };

  const addReply = (articleId: string, commentId: string, reply: string, setReply: any) => {
    const replyData = {
      userId: {
        firstname: userDetails?.firstname ? userDetails?.firstname : "",
        lastname: userDetails?.lastname ? userDetails?.lastname : "",
        photoId: {
          url: userDetails?.photoId?._id ? Util.fileURL(userDetails?.photoId?._id) : "",
          _id: userDetails?.photoId?._id ? userDetails?.photoId?._id : "",
          type: userDetails?.photoId?.type ? userDetails?.photoId?.type : "",
        },
        role: userDetails?.role,
        _id: userDetails?._id ? userDetails?._id : "",
      },
      reply: reply,
      date: new Date().toISOString(),
    };

    const commentList = articleDetails?.comments ? articleDetails?.comments : [];

    const comment = commentList.filter(comment => comment._id == commentId);

    comment[0].replies = comment[0].replies?.concat(replyData);
    articleDetails?.comments.map(a => (a._id == commentId ? comment : a));

    const data = {
      comment: articleDetails?.comments,
    };

    setReply("");

    ArticleService.updateCommentReply(articleId, data).then(res => {
      if (res.success) {
        setArticleDetails(res.data);
      } else {
        setArticleDetails(articleDetails);
      }
    });
  };

  const handleComments = (event: any, articleId: string | undefined) => {
    if (event.keyCode == 13) {
      addComment(articleId);
    }
  };

  const copyToClipboard = async (text: string) => {
    if ("clipboard" in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand("copy", true, text);
    }
  };

  const handleCopy = () => {
    const copiedText = document.getElementById("article-content")?.innerHTML.replace(/(<([^>]+)>)/gi, "");
    const copiedTextInString = copiedText?.toString();

    if (copiedTextInString != undefined || copiedTextInString != null || copiedTextInString != "") {
      copyToClipboard(copiedTextInString!).then(() => {
        toast.success("Text copied to clipboard", {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
        });
      });
    } else {
      toast.error("Failed to copy the text to clipboard", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
      });
    }
  };

  return (
    <React.Fragment>
      {/* <section className="top-bar">
        <div className="container">
          <div className="row rowWidth">
            <div className="col-6 col-md-8 ">
              <div className="contact-info">
                <p className="contact-item2 mainText2">Phone: +1 (980) 890 7522</p>
                <p className="contact-item2 mainText md-hide">Email: info@mylavni.com</p>
              </div>
            </div>
            <div className="col-6 col-md-4  text-md-end social-icons-container01">
              <a href="https://www.instagram.com/mylavni/">
                <span className="iconify" data-icon="uil:instagram"></span>
              </a>
              <a href="https://www.facebook.com/lavnihealth">
                <span className="iconify" data-icon="uil:facebook-f"></span>
              </a>
              <a href="https://www.linkedin.com/company/lavni/">
                <span className="iconify" data-icon="uil:linkedin"></span>
              </a>
            </div>
          </div>
        </div>
      </section> */}
      <header className="wrapper bg-light2">
        <nav className="navbar navbar-expand-lg classNameic transparent navbar-light">
          <div className="container flex-lg-row flex-nowrap align-items-center">
            <div className="navbar-brand w-100">
              <a href="/home">
                <img
                  src="./../../static/assets/img/logo-dark.png"
                  style={{ height: "50px" }}
                  alt="Lavni helps you find a mental health professional
                that understands your unique experience"
                />
              </a>
            </div>
            <div className="navbar-collapse offcanvas offcanvas12 offcanvas-nav offcanvas-start">
              <div className="offcanvas-header d-lg-none">
                <h3 className="text-white fs-30 mb-0">Lavni</h3>
                <button type="button" className="btn-close btn-close-white" data-bs-dismiss="offcanvas" aria-label="Close"></button>
              </div>
              <div className="offcanvas-body ms-lg-auto d-flex flex-column h-100">
                <ul className="navbar-nav mainText navbar-nav123 fontHead mb-2">
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/home">
                      Home
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/about">
                      About
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/service">
                      Service
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/faq">
                      FAQ
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/blogs/">
                      Blogs
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/pricing/">
                      Pricing
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/contact">
                      Contact
                    </a>
                  </li>
                  <li className="nav-item">
                    <a className="nav-link navlin001 mainText" href="/therapist-onboarding">
                      Therapist
                    </a>
                  </li>
                  <li className="nav-item d-lg-none d-md-none">
                    <a className="nav-link navlin001 mainText" href="/signin">
                      Sign In
                    </a>
                  </li>
                  <li className="nav-item d-lg-none d-md-none">
                    <a className="nav-link navlin001 mainText" href="/main">
                      Sign Up
                    </a>
                  </li>
                </ul>

                <div className="offcanvas-footer d-lg-none">
                  <div>
                    <div className="mb-2">
                      <a href="tel:+19808907522" className="link-inverse">+1 (980) 890 7522</a>
                    </div>
                    <div className="mb-2">
                      <a href="mailto:info@mylavni.com" className="link-inverse">info@mylavni.com</a>
                    </div>
                    <nav className="nav social social-white mt-3">
                      <a href="https://www.instagram.com/mylavni/">
                        <span className="iconify" data-icon="uil:instagram"></span>
                      </a>
                      <a href="https://www.facebook.com/lavnihealth">
                        <span className="iconify" data-icon="uil:facebook-f"></span>
                      </a>
                      <a href="https://www.linkedin.com/company/lavni/">
                        <span className="iconify" data-icon="uil:linkedin"></span>
                      </a>
                    </nav>
                  </div>
                </div>
              </div>
            </div>

            <div className="navbar-other ms-lg-4">
              <ul className="navbar-nav flex-row align-items-center ms-auto">
                <li className="nav-item d-none d-md-block">
                  <a href="/signin" className="btn btn-sm btn-outline-primary mainText rounded-pill3">
                    Sign In
                  </a>
                </li>
                <li className="nav-item d-none d-md-block">
                  <a href="/home/#client-signup" className="btn btn-sm btn-primary rounded-pill3 mainText ml-B2 ">
                    Sign Up
                  </a>
                </li>
                <li className="nav-item d-lg-none">{/* <button className="hamburger offcanvas-nav-btn" onClick={toggleOffcanvas}><span></span></button> */}</li>
              </ul>
            </div>
          </div>
        </nav>
      </header>
      <div className="container pt-3">
        <title>Article Details | Lavni</title>
        <Container fluid>
          {viewtype == "grid" ? <Breadcrumbs title="Article" breadcrumbItem="Article-Grid" /> : <Breadcrumbs title="Article" breadcrumbItem="Article View" />}
          {!isLoading ? (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="pt-3">
                      <div className="row justify-content-center">
                        <div className="col-xl-8">
                          <div>
                            <div className="text-center">
                              <div className="mb-4">
                                <Link to="#" className="badge bg-light font-size-12"></Link>
                              </div>
                              <h4>{articleDetails?.articleTitle}</h4>
                              <p className="text-muted mb-4">
                                <i className="mdi mdi-calendar me-1"></i>
                                {moment(articleDetails?.createdAt.toString()).format("YYYY/MM/DD")}
                              </p>
                            </div>

                            <hr />
                            <div className="text-center">
                              <Row>
                                <Col sm={4}>
                                  <div>
                                    <p className="text-muted mb-2">Areas Of Specialization</p>
                                    <h5 className="font-size-15">
                                      {articleDetails?.articleTags.map((item, index) => {
                                        return (
                                          <span key={index}>
                                            {index > 0 && <span>,&nbsp;</span>}

                                            {item.experienceTag}
                                          </span>
                                        );
                                      })}
                                    </h5>
                                  </div>
                                </Col>
                                <Col sm={4}>
                                  <div className="mt-4 mt-sm-0">
                                    <p className="text-muted mb-2">Post By</p>
                                    <div className="d-flex justify-content-center align-items-center">
                                      {articleDetails?.createdBy?.photoId ? (
                                        <img src={Util.fileURL(articleDetails?.createdBy?.photoId?._id)} className="block avatar-img imageFit" />
                                      ) : (
                                        <img src={defaultImage} className="block avatar-img" />
                                      )}
                                      <h5 className="font-size-15 px-2 mb-0">
                                        {articleDetails?.createdBy?.firstname || ""}
                                        &nbsp;
                                        {articleDetails?.createdBy?.lastname || ""}
                                      </h5>
                                    </div>
                                  </div>
                                </Col>
                                <Col sm={4}>
                                  <div className="mt-4 mt-sm-0">
                                    <p className="text-muted mb-2">Ratings</p>
                                    <div className="d-flex justify-content-center align-items-center">
                                      <Rating initialValue={rating1} size={20} transition fillColor={"#F2C94C"} readonly={true} />
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            </div>
                            <hr />

                            {articleDetails?.uploadId && articleDetails?.uploadId.type.indexOf("image") !== -1 ? (
                              <div className="player-wrapper">
                                <div className="my-5">
                                  <img src={Util.fileURL(articleDetails?.uploadId._id)} className="img-thumbnail mx-auto d-block" />
                                </div>
                              </div>
                            ) : (
                              <div className="player-wrapper">
                                {articleDetails?.vimeoId && (
                                  <>
                                    {transcodeStatus == "" ? (
                                      <div className="pending-transcoding">Loading...</div>
                                    ) : (
                                      <>
                                        {transcodeStatus == "in_progress" ? (
                                          <div className="pending-transcoding">
                                            {isRefreshing ? (
                                              "Checking Status... "
                                            ) : (
                                              <div>
                                                Video is still transcoding! Will available in a minute! &nbsp;
                                                <i
                                                  className="fa fa-refresh text-primary refresh-icon"
                                                  onClick={() => {
                                                    refreshTranscodeStatus(vimeoId);
                                                  }}
                                                ></i>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="video-responsive" style={{ width: "640px" }}>
                                            <iframe
                                              src={"https://player.vimeo.com/video/" + vimeoId}
                                              className="video-responsive-item"
                                              frameBorder="0"
                                              allow="autoplay; fullscreen; picture-in-picture"
                                              allowFullScreen
                                              title="Lavni Health"
                                            ></iframe>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            <div className="flex flex-row flex-wrap font-size-15">
                              {articleDetails?.hashTags.map((item, index) => {
                                return (
                                  <div className="py-2" key={index} style={{ wordBreak: "break-word" }}>
                                    <span className="hash-tag">{item as any}</span>
                                  </div>
                                );
                              })}
                            </div>

                            <hr />

                            <div className="mt-4">
                              <div className="text-muted font-size-14 d-flex">
                                <div
                                  id="article-content"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(articleDetails?.articleBody),
                                  }}
                                ></div>
                              </div>

                              <hr />

                              {/* {userDetails?._id === articleDetails?.createdBy?._id ? (
                                <div className="mt-3 button-sec">
                                  <div className="p-1">
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      onClick={() => {
                                        deleteArticle(articleId);
                                      }}
                                    >
                                      <i className="bx bx-folder-minus font-size-16 align-middle me-2"></i>
                                      &nbsp; Delete Post
                                    </button>
                                  </div>
                                  <div className="p-1">
                                    <button type="button" className="btn btn-primary" onClick={() => redirectToEdit(articleId)}>
                                      <i className="bx bxs-edit-alt font-size-16 align-middle me-2"></i>
                                      &nbsp; Edit Post
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <></>
                              )} */}

                              {/* <div className="mt-3 like-bar" style={{ alignItems: "center" }}>
                                <h5 className="font-size-15 like-bar">
                                  {isLiked() ? (
                                    <div
                                      className="like-menu"
                                      onClick={() => {
                                        likePost(articleDetails?._id);
                                      }}
                                      title="Like"
                                    >
                                      <img src={likedButton} />
                                      &nbsp;
                                    </div>
                                  ) : (
                                    <div
                                      className="like-menu"
                                      onClick={() => {
                                        likePost(articleDetails?._id);
                                      }}
                                    >
                                      <img src={likeButton} />
                                      &nbsp;
                                    </div>
                                  )}

                                  <div>{articleDetails?.likedBy?.length} Likes&nbsp;</div>
                                </h5>
                                <h5
                                  className="font-size-15"
                                  style={{
                                    justifyContent: "flex-end",
                                    cursor: "Pointer",
                                  }}
                                  onClick={() => {
                                    setShowShareModal(!showShareModal);
                                  }}
                                >
                                  &nbsp;
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M16.8916 13.7777C16.3881 13.7781 15.8923 13.9011 15.4469 14.1362C15.0015 14.3712 14.62 14.7113 14.3352 15.1269L10.0008 13.1677C10.3087 12.4235 10.3099 11.5876 10.0041 10.8425L14.3325 8.87324C14.7546 9.48438 15.3815 9.92383 16.0995 10.1119C16.8176 10.2999 17.5791 10.2241 18.2461 9.89819C18.9131 9.57227 19.4413 9.01784 19.7349 8.33545C20.0285 7.65306 20.0681 6.88793 19.8464 6.17883C19.6248 5.46974 19.1566 4.86365 18.5268 4.47051C17.8971 4.07737 17.1474 3.92322 16.4138 4.03601C15.6802 4.14881 15.0113 4.52109 14.5284 5.08531C14.0455 5.64954 13.7806 6.36834 13.7818 7.11134C13.7846 7.28719 13.8024 7.46249 13.8351 7.63531L9.23369 9.72853C8.79176 9.31403 8.2385 9.03779 7.6419 8.93375C7.04529 8.8297 6.43132 8.90239 5.87542 9.14287C5.31951 9.38335 4.84588 9.78115 4.5127 10.2874C4.17953 10.7937 4.00134 11.3863 4.00001 11.9926C3.99868 12.5988 4.17427 13.1923 4.50522 13.7C4.83616 14.2077 5.30804 14.6076 5.86289 14.8505C6.41774 15.0934 7.03138 15.1688 7.62844 15.0674C8.22549 14.966 8.77995 14.6922 9.2237 14.2796L13.8371 16.3649C13.805 16.5375 13.7874 16.7126 13.7845 16.8882C13.7843 17.5035 13.9665 18.1052 14.3079 18.6169C14.6494 19.1286 15.1347 19.5275 15.7026 19.763C16.2705 19.9986 16.8955 20.0603 17.4984 19.9403C18.1013 19.8202 18.6551 19.5239 19.0898 19.0888C19.5244 18.6536 19.8204 18.0992 19.9403 17.4956C20.0602 16.8921 19.9986 16.2664 19.7633 15.6979C19.528 15.1294 19.1296 14.6435 18.6184 14.3017C18.1072 13.9599 17.5063 13.7775 16.8916 13.7777ZM16.8916 5.33343C17.2428 5.3333 17.5863 5.43746 17.8784 5.63274C18.1706 5.82801 18.3983 6.10564 18.5328 6.4305C18.6673 6.75536 18.7026 7.11286 18.6342 7.45778C18.5657 7.8027 18.3966 8.11956 18.1483 8.36827C17.8999 8.61698 17.5835 8.78638 17.239 8.85503C16.8944 8.92369 16.5373 8.88852 16.2128 8.75397C15.8882 8.61943 15.6108 8.39155 15.4156 8.09916C15.2204 7.80677 15.1163 7.463 15.1163 7.11134C15.1166 6.64003 15.3038 6.18812 15.6366 5.85479C15.9694 5.52146 16.4208 5.33396 16.8916 5.33343ZM7.12544 13.7777C6.77416 13.7778 6.43073 13.6736 6.13859 13.4783C5.84645 13.2831 5.61871 13.0054 5.48419 12.6806C5.34967 12.3557 5.31441 11.9982 5.38286 11.6533C5.45131 11.3084 5.6204 10.9915 5.86875 10.7428C6.11709 10.4941 6.43354 10.3247 6.77806 10.2561C7.12258 10.1874 7.4797 10.2226 7.80425 10.3571C8.12881 10.4917 8.40621 10.7195 8.60139 11.0119C8.79656 11.3043 8.90074 11.6481 8.90074 11.9997C8.90021 12.471 8.71302 12.9228 8.38022 13.2561C8.04742 13.5894 7.59618 13.7769 7.12544 13.7777ZM16.8916 18.6661C16.5403 18.6661 16.1969 18.5618 15.9049 18.3664C15.6128 18.1711 15.3852 17.8934 15.2508 17.5685C15.1164 17.2437 15.0812 16.8862 15.1497 16.5413C15.2183 16.1964 15.3874 15.8796 15.6358 15.631C15.8841 15.3823 16.2006 15.213 16.5451 15.1444C16.8896 15.0758 17.2467 15.111 17.5712 15.2456C17.8957 15.3802 18.1731 15.608 18.3682 15.9004C18.5634 16.1928 18.6675 16.5365 18.6675 16.8882C18.6672 17.3596 18.48 17.8116 18.147 18.1449C17.814 18.4783 17.3625 18.6657 16.8916 18.6661Z"
                                      fill="#6B7280"
                                    />
                                  </svg>
                                  Share&nbsp;
                                </h5>
                                <h5 className="font-size-15">
                                  &nbsp;
                                  <i className="bx bx-message-dots text-muted align-middle me-1"></i>
                                  Comments :
                                </h5>
                              </div> */}

                              {/* <div className="mt-3">
                                <div className="d-flex py-3 border-top">
                                  <div className="avatar-xs me-3">
                                    <img
                                      src={Util.fileURL(userDetails?.photoId?._id)}
                                      className="img-fluid d-block rounded-circle imageFit"
                                      style={{ height: "2rem" }}
                                    />
                                  </div>
                                  <div className="flex justify-content-between w-100" style={{ flexDirection: "row" }}>
                                    <div className="px-2 w-100">
                                      <input
                                        type="text"
                                        className="form-control shadow-sm bg-white rounded"
                                        placeholder="Add a comment..."
                                        name="comment"
                                        value={comment}
                                        onChange={event => {
                                          setComment(event?.target.value);
                                        }}
                                        onKeyDown={event => {
                                          handleComments(event, articleDetails?._id);
                                        }}
                                      />
                                    </div>
                                    <button
                                      className="btn btn-primary px-2"
                                      onClick={() => {
                                        addComment(articleDetails?._id);
                                      }}
                                    >
                                      Comment
                                    </button>
                                  </div>
                                </div>
                                {articleDetails?.comments
                                  .sort((a, b) => (a.date > b.date ? -1 : 1))
                                  .map((item: any, index: number) => {
                                    return (
                                      <CommentSection
                                        comment={item.comment}
                                        date={moment(item.date).format("YYYY/MM/DD")}
                                        imageUrl={item.userId.photoId === null ? null : Util.fileURL(item.userId.photoId?._id)}
                                        firstname={
                                          item.userId.firstname == null || item.userId.firstname == undefined || item.userId.firstname == ""
                                            ? item.userId.role
                                            : item.userId.firstname
                                        }
                                        lastname={
                                          item.userId.lastname == null || item.userId.lastname == undefined || item.userId.lastname == ""
                                            ? undefined
                                            : item.userId.lastname
                                        }
                                        replies={item.replies}
                                        commentId={item._id}
                                        articleId={articleDetails._id}
                                        addReply={addReply}
                                        key={index}
                                      />
                                    );
                                  })}
                              </div> */}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <Spinner />
          )}
        </Container>
      </div>
      {showShareModal ? (
        <ShareModal
          title={articleDetails?.articleTitle}
          show={showShareModal}
          close={() => setShowShareModal(!showShareModal)}
          copyToClipBoard={copyToClipboard}
          handleCopy={handleCopy}
        />
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

export default PublicArticleDetails;
